/** Public register of source-linked offers still active, separate from reviewed research notes. */
import { useSearchParams } from "react-router";
import { moneyMinor } from "@oc/copy";
import { h, Button, EmptyState, HelpBadge, Message, Skeleton, StatusBadge, type ReactNode } from "@oc/ui";
import { api, type ActiveOffer } from "../api.ts";
import { ACTIVE_OFFERS } from "../copy.ts";
import { fmtDay, useLoad } from "../hooks.ts";
import { Page } from "../layout.ts";
import { useRegion } from "../region.ts";
import { badgeState, statusLabel } from "./landing.ts";

function OfferCard({ offer, reportHref, detailHref }: { offer: ActiveOffer; reportHref: string | null; detailHref: string }): ReactNode {
  const price = offer.asking_price_minor !== null && offer.currency
    ? moneyMinor(offer.asking_price_minor, offer.currency) : "Not stated";
  const sector = offer.sector?.replaceAll("_", " ") ?? "Sector not stated";
  const sectorLabel = `${sector[0]!.toUpperCase()}${sector.slice(1)}`;
  const sourceName = ({ anybusiness: "AnyBusiness", bsale: "Bsale", seek_business: "SEEK Business" } as Record<string, string>)[offer.platform] ?? offer.platform.replaceAll("_", " ");
  return h("article", { className: "oc-offer-card" },
    h("div", { className: "oc-offer-card-top" },
      h("span", { className: "oc-offer-source" }, `${sourceName} · ${offer.external_ref}`),
      h(StatusBadge, { state: badgeState(offer.liveness_status), label: statusLabel(offer.liveness_status) })),
    h("h2", null, offer.published_note?.headline ?? `${sectorLabel} in ${offer.location}`),
    h("div", { className: "oc-offer-location" }, offer.location, " · ", sectorLabel),
    h("div", { className: "oc-offer-price" },
      h("span", null, "Advertised asking"), h("strong", { className: "oc-tnum" }, price)),
    h("div", { className: "oc-offer-meta" },
      h("span", null, `${offer.facts_count} public fact${offer.facts_count === 1 ? "" : "s"} recorded`),
      h("span", null, offer.days_observed ? `${offer.days_observed} day${offer.days_observed === 1 ? "" : "s"} tracked` : "Tracking started")),
    h("div", { className: "oc-offer-checked" }, `Status ${offer.liveness_checked_at ? `checked ${fmtDay(offer.liveness_checked_at)}` : "not yet checked"}`),
    h("div", { className: "oc-offer-links" },
      h("a", { href: detailHref }, "View tracked history →"),
      reportHref ? h("a", { href: reportHref }, ACTIVE_OFFERS.report, " ↗") : null,
      offer.source_url ? h("a", { href: offer.source_url, target: "_blank", rel: "noopener noreferrer" }, ACTIVE_OFFERS.source, " ↗") : null));
}

export function ActiveOffersPage(): ReactNode {
  const r = useRegion();
  const [params, setParams] = useSearchParams();
  const parsed = Number(params.get("page") ?? "1");
  const page = Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
  const loaded = useLoad(() => api.activeOffers(r.code, page), [r.code, page]);
  const result = loaded.data;
  const total = result?.total_count ?? 0;
  const start = (page - 1) * (result?.per_page ?? 24) + 1;
  const end = Math.min(total, page * (result?.per_page ?? 24));
  return h(Page, { screen: "active-offers" },
    h("section", { className: "oc-offers-page" }, h("div", { className: "oc-wrap" },
      h("div", { className: "oc-offers-intro" },
        h("div", { className: "oc-offers-kicker" }, ACTIVE_OFFERS.eyebrow, h(HelpBadge, { id: "web.active-offers" })),
        h("h1", null, ACTIVE_OFFERS.heading),
        h("p", null, ACTIVE_OFFERS.lead),
        h("a", { href: r.href("/"), className: "oc-offers-back" }, "← ", ACTIVE_OFFERS.return)),
      loaded.error ? h("div", { className: "oc-offers-message" },
        h(Message, { kind: "hard", what: "The active offers could not be loaded.", todo: loaded.error }),
        h(Button, { label: "Try again", onClick: loaded.reload })) : null,
      loaded.error ? null : loaded.loading ? h("div", { className: "oc-offers-message" }, h(Skeleton, { rows: 5 }))
        : result && result.listings.length === 0 ? h("div", null,
          h(EmptyState, { title: page === 1 ? ACTIVE_OFFERS.no_items : "There are no more offers on this page.", body: "The latest published research remains available from the home page." }),
          page > 1 ? h("button", { type: "button", className: "btn-2", onClick: () => setParams({ page: String(page - 1) }) }, "← Previous page") : null)
        : h("div", null,
          h("div", { className: "oc-offers-count", "aria-live": "polite" }, `${start}–${end} of ${total} tracked active offers`),
          h("div", { className: "oc-offers-grid" }, ...(result?.listings ?? []).map((offer) =>
            h(OfferCard, { key: `${offer.platform}-${offer.external_ref}`, offer,
              detailHref: r.href(`/offers/${encodeURIComponent(offer.platform)}/${encodeURIComponent(offer.external_ref)}`),
              reportHref: offer.published_note ? r.href(`/research/${offer.published_note.slug}`) : null }))),
          h("div", { className: "oc-offers-pagination" },
            page > 1 ? h("button", { type: "button", onClick: () => setParams({ page: String(page - 1) }) }, "← Previous") : null,
            result && page * result.per_page < total ? h("button", { type: "button", onClick: () => setParams({ page: String(page + 1) }) }, "Next →") : null)),
      h("p", { className: "oc-offers-footnote" }, "Status reflects the last dated source check. Prices and other advertisement claims are not verified owner financials."))));
}
